// Lightweight i18n: dictionary lookup with {var} interpolation, English
// fallback, and a React context/hook so components can call `t("key")`.
//
// Adding a new language: import its dictionary in `translations` and add
// { code, name, flag } to `LANGUAGES`. The keys must mirror the English file.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "./en";
import es from "./es";
import pt from "./pt";

export const LANGUAGES = [
  { code: "en", name: "English",    flag: "🇺🇸" },
  { code: "es", name: "Español",    flag: "🇪🇸" },
  { code: "pt", name: "Português",  flag: "🇵🇹" },
];

const translations = { en, es, pt };
const STORAGE_KEY = "ucenter.lang";

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`
  );
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) return saved;
    const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
    return translations[browser] ? browser : "en";
  });

  const setLang = (next) => {
    if (!translations[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      /* ignore storage errors */
    }
    if (typeof document !== "undefined") document.documentElement.lang = next;
  };

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const dict = translations[lang] || translations.en;
    const fallback = translations.en;
    const t = (key, vars) => {
      const raw = dict[key] ?? fallback[key] ?? key;
      return interpolate(raw, vars);
    };
    return { lang, setLang, t };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
